import { styled, alpha } from "@mui/material/styles";
import {
  Box,
  TableCell,
  TextField,
  IconButton,
  Table,
  TableRow,
  Button,
} from "@mui/material";

// Update the color constants at the top of TableStyles.js
export const tableColors = {
  header: {
    background: () => 'rgb(44, 52, 123)',
    text: () => '#ffffff',
  },
  row: {
    background: (theme) =>
      theme.palette.mode === "dark"
        ? theme.palette.background.default // Slightly lighter for rows in dark mode
        : theme.palette.common.white, // White for rows in light mode
    hover: (theme) =>
      theme.palette.mode === "dark"
        ? alpha(theme.palette.action.hover, 0.15) // Reduced opacity for dark mode only
        : theme.palette.grey[50], // Keep light mode as is
    text: (theme) => theme.palette.text.primary, // Always primary for row values
  },
  border: (theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.divider
      : theme.palette.grey[200],
  text: {
    placeholder: (theme) =>
      theme.palette.mode === "dark"
        ? theme.palette.text.secondary
        : theme.palette.text.secondary,
  },
  divider: (theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.divider
      : theme.palette.grey[200],
};

// Colors that adapt to theme mode
const getColors = (theme) => ({
  background:
    theme.palette.mode === "dark" ? theme.palette.background.paper : "#FFFFFF",
  border:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.divider, 0.2)
      : "#E2E8F0",
  headerBg:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.default, 0.6)
      : "#F8FAFC",
  text: {
    primary: theme.palette.text.primary,
    secondary: theme.palette.text.secondary,
    placeholder: theme.palette.text.disabled,
  },
  button: {
    background:
      theme.palette.mode === "dark"
        ? theme.palette.primary.dark
        : theme.palette.primary.main,
    text: theme.palette.common.white,
    hover:
      theme.palette.mode === "dark"
        ? theme.palette.primary.main
        : theme.palette.primary.dark,
  },
  hover:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.action.hover, 0.2)
      : alpha("#f7f9faff", 0.6),
  action: {
    hover:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.action.hover, 0.2)
        : alpha(theme.palette.action.hover, 0.1),
  },
});

export const TableWrapper = styled(Box)(({ theme, compact }) => ({
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  overflow: "hidden",
  padding: compact ? 0 : theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  "& .css-1ddbrb2-MuiPaper-root-MuiTableContainer-root": {
    borderRadius: "0 !important",
  },
}));

export const StyledTable = styled(Table)(({ theme }) => ({
  borderCollapse: "separate",
  borderSpacing: "0 1px",
  backgroundColor: theme.palette.background.paper,
  width: "100%",
  tableLayout: "fixed",
}));

export const HeaderCell = styled(TableCell)(({ theme, fontSize = "13px" }) => ({
  backgroundColor: tableColors.header.background(theme),
  color: tableColors.header.text(theme),
  fontWeight: 700,
  padding: `calc(${fontSize} * 0.3) calc(${fontSize} * 1)`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  fontSize: `calc(${fontSize} * 0.9)`,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 2,
  height: `calc(${fontSize} * 2)`,
  lineHeight: "1",
}));

export const StyledTableCell = styled(TableCell)(
  ({ theme, column, align = "left", fontSize = "13px" }) => ({
    padding: `calc(${fontSize} * 0.3) calc(${fontSize} * 1)`,
    backgroundColor: tableColors.header.background(theme),
    position: "relative",
    color: tableColors.header.text(theme),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize,
    height: `calc(${fontSize} * 2)`,
    textAlign: align,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",

    "& .header-content": {
      display: "flex",
      alignItems: "center",
      justifyContent:
        align === "center"
          ? "center"
          : align === "right"
            ? "flex-end"
            : "flex-start",
      width: "100%",
      gap: `calc(${fontSize} * 0.3)`,
      fontSize: `calc(${fontSize} * 0.9)`,
      fontWeight: 700,
      color: "tableColors.header.text(theme)",
      lineHeight: "1",
      textTransform: "uppercase",
      letterSpacing: "0.3px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",

      "& .MuiSvgIcon-root": {
        fontSize: `calc(${fontSize} * 1)`,
        visibility: "hidden",
        color: theme.palette.text.secondary,
        flexShrink: 0, // Prevent icon from shrinking
      },
    },

    "&:hover": {
      "& .header-content .MuiSvgIcon-root": {
        visibility: "visible",
      },
    },

    '& .header-content .MuiSvgIcon-root[data-active="true"]': {
      visibility: "visible",
      color: theme.palette.primary.main,
    },
  })
);

export const DataCell = styled(StyledTableCell)(({ theme }) => ({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: '16px 32px',
  position: 'relative',

  '& .cell-content': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    fontWeight: 600,
    
  },
  '&:hover':{
    backgroundColor: '#e9eeffff',
    cursor: 'pointer',
  }
}));

export const FixedCell = styled(TableCell)(
  ({ theme, fixed, fontSize = "13px" }) => ({
    padding: `calc(${fontSize} * 0.3) calc(${fontSize} * 1)`,
    borderBottom: `1px dotted ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : tableColors.header.background(theme),
    color: tableColors.header.text(theme),
    height: `calc(${fontSize} * 2)`,
    whiteSpace: "nowrap",
    lineHeight: "1",
    fontSize,

    ...(fixed === "left" && {
      position: "sticky",
      left: 0,
      zIndex: theme.zIndex.appBar + 1,
      backgroundColor:
        theme.palette.mode === "dark"
          ? theme.palette.background.default
          : tableColors.row.background(theme),
      "&.MuiTableCell-head": {
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar + 2,
        backgroundColor: tableColors.header.background(theme),
        color: tableColors.header.text(theme),
      },
      ".MuiTableRow-root:hover &": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? theme.palette.background.paper
            : tableColors.row.hover(theme),
      },
    }),

    ...(fixed === "right" && {
      position: "sticky",
      right: 0,
      zIndex: theme.zIndex.appBar + 1,
      backgroundColor:
        theme.palette.mode === "dark"
          ? theme.palette.background.default
          : tableColors.row.background(theme),
      "&.MuiTableCell-head": {
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar + 2,
        backgroundColor: tableColors.header.background(theme),
        color: tableColors.header.text(theme),
      },
      ".MuiTableRow-root:hover &": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? theme.palette.background.paper
            : tableColors.row.hover(theme),
      },
    }),
  })
);

export const StyledTableRow = styled(TableRow)(
  ({ theme, fontSize = "13px" }) => ({
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : tableColors.row.background(theme),
    minHeight: 48,
    height: `calc(${fontSize} * 2)`,
    borderBottom: `1px solid ${theme.palette.divider}`,

    '&:hover':{
      "& td": {
        backgroundColor: "#F0F4FF !important",
        transition: "background-color 0.25s ease",
      },
    },

    "& td": {
      padding: `calc(${fontSize} * 0.2) calc(${fontSize} * 0.8)`,
      border: "none",
      color: tableColors.row.text(theme),
      fontSize,
      backgroundColor: "inherit",
      minHeight: 38,
      height: `calc(${fontSize} * 2)`,
      lineHeight: "1.4",
      wordBreak: 'break-word',
      whiteSpace: 'normal',
    },
  })
);

export const TableContainer = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : "#FFFFFF",
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  boxShadow: theme.shadows[3],
  border: `1px solid ${theme.palette.divider}`,
  

  [theme.breakpoints.down("sm")]: {
    borderRadius: 0,
    border: "none",
  },


  "& .MuiTableContainer-root": {
    maxHeight: "calc(100vh - 280px)",
    overflow: "auto",
    scrollbarWidth: "thin",


     marginLeft: theme.spacing(4),
     marginRight: theme.spacing(4),
    
    [theme.breakpoints.down("sm")]: {
      maxHeight: "calc(100vh - 200px)",
    },

    "& .MuiTable-root": {
      tableLayout: "auto",
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,

      
      [theme.breakpoints.down("sm")]: {
        display: "block",
        "& thead": { display: "none" }, // Hide header on mobile
        "& tbody": { display: "block" },
        "& tr": {
          display: "block",
          marginBottom: theme.spacing(2),
          boxShadow: theme.shadows[1],
          borderRadius: theme.shape.borderRadius,
          backgroundColor: theme.palette.background.paper,
        },
        "& td": {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "left", //changed
          padding: theme.spacing(1.5),
          borderBottom: `1px solid ${theme.palette.divider}`,

          "&:before": {
            content: "attr(data-label)",
            fontWeight: 600,
            marginRight: theme.spacing(2),
            minWidth: "120px",
          },
        },
      },
    },
  },
}));

export const TableHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "8px 16px",
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.5)
      : alpha(theme.palette.background.paper, 0.8),
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: "48px",
}));

export const TableHeaderCell = styled("div")(({ theme, width }) => ({
  flex: width || 1,
  display: "flex",
  alignItems: "left",
  fontSize: "12px",
  fontWeight: 600,
  color: theme.palette.mode === "dark" ? "#c9d1d9" : "#44444f",
  padding: "0 8px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  textTransform: "uppercase",
  letterSpacing: "0.5px",

  "& .MuiSvgIcon-root": {
    fontSize: "14px",
    marginLeft: "4px",
    color: theme.palette.text.secondary,
  },
}));

export const TableToolbar = styled(Box)(({ theme, compact }) => ({
  padding: compact ? "6px 10px" : theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: compact ? 6 : theme.spacing(2),
}));

export const SearchAndFilterContainer = styled(Box)(({ theme, compact }) => ({
  padding: compact ? "2px 0 6px" : theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: compact ? 8 : theme.spacing(2),
  flexWrap: "wrap",
}));

export const SearchField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "compact",
})(({ theme, compact }) => ({
  maxWidth: compact ? 260 : 320,
  "& .MuiOutlinedInput-root": {
    height: compact ? 30 : 36,
    backgroundColor: '#F9FAFB',
    borderRadius: 6,
    border: '1px solid #E5E7EB',
    transition: theme.transitions.create(["border-color"]),
    "&:hover": {
      borderColor: '#D1D5DB',
    },
    "&.Mui-focused": {
      borderColor: '#5d66ceff',
      boxShadow: 'none',
    },
    "& fieldset": {
      border: 'none',
    },
  },
  "& .MuiInputBase-input": {
    fontSize: compact ? "0.8rem" : "0.875rem",
    padding: compact ? "4px 10px" : "8px 12px",
    color: '#1E1E1E',
    "&::placeholder": {
      color: '#9CA3AF',
      opacity: 1,
    },
  },
}));

export const ActionButton = styled(IconButton)(
  ({ theme, color = "default" }) => ({
    padding: 8,
    backgroundColor: "transparent",
    color:
      color === "default"
        ? getColors(theme).text.secondary
        : theme.palette[color]?.main || color,
    transition: theme.transitions.create(["background-color", "transform"]),
    "&:hover": {
      backgroundColor: alpha(getColors(theme).hover, 0.8),
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
    "&.Mui-disabled": {
      color: theme.palette.action.disabled,
    },
  })
);

export const ActionButtonGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "inherit",
}));

export const HeaderSearchInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    height: 32,
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.background.paper
        : "#FFFFFF",
    "& fieldset": {
      borderColor:
        theme.palette.mode === "dark"
          ? theme.palette.divider
          : tableColors.border,
      borderRadius: 6,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "& input": {
      fontSize: "0.875rem",
      color: theme.palette.text.primary,
      "&::placeholder": {
        color:
          theme.palette.mode === "dark"
            ? theme.palette.text.secondary
            : tableColors.text.placeholder,
        opacity: 1,
      },
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "4px 8px",
  },
  "& .MuiInputAdornment-root": {
    color:
      theme.palette.mode === "dark"
        ? theme.palette.text.secondary
        : tableColors.text.placeholder,
    "& .MuiIconButton-root": {
      padding: 4,
      color:
        theme.palette.mode === "dark"
          ? theme.palette.text.secondary
          : tableColors.text.placeholder,
      "&:hover": {
        color: theme.palette.primary.main,
      },
    },
  },
}));

export const SortIconWrapper = styled("div")(
  ({ theme, active, direction }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
    transition: theme.transitions.create("transform"),
    transform: direction === "desc" ? "rotate(180deg)" : "none",
    marginLeft: 4,

    "& .MuiSvgIcon-root": {
      fontSize: "0.875rem",
      transition: theme.transitions.create(["visibility", "color"]),
      // Set data-active attribute when sort is active
      ...(active && {
        "data-active": "true",
      }),
    },
  })
);

export const TablePagination = styled("div")(({ theme }) => ({
  padding: "0 8px",
  height: 22,
  minHeight: 22,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: theme.spacing(0.75),
  color: "#444",
  backgroundColor: "#eeeeee",
  "& .MuiTablePagination-root": {
    alignItems: "center",
    minHeight: 22,
  },
  "& .MuiTablePagination-toolbar": {
    alignItems: "center",
    minHeight: 22,
  },
  "& .MuiSelect-select": {
    minWidth: 36,
    padding: "0 2px 0 4px",
    display: "flex",
    alignItems: "center",
    borderRadius: theme.shape.borderRadius,
    border: "none",
    backgroundColor: theme.palette.action.hover,
    color: "#3F49A7",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: alpha(theme.palette.action.hover, 0.8),
    },
  },
  "& .pagination-arrows": {
    display: "flex",
    gap: theme.spacing(1),
    "& button": {
      padding: 3,
      color: theme.palette.text.secondary,
      "&:disabled": {
        color: theme.palette.text.disabled,
      },
      "&:hover:not(:disabled)": {
        color: theme.palette.primary.main,
        backgroundColor: "transparent",
      },
    },
  },
  "& .MuiPaginationItem-root": {
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
    color: "#333",
    backgroundColor: "#fff",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#F0F4FF",
    },
    "&.Mui-selected": {
      backgroundColor: "#3F51B5", // active blue
      color: "#fff",
      border: "1px solid #3F51B5",
      "&:hover": {
        backgroundColor: "#303F9F",
      },
    },
  },
}));

export const TableTitle = styled(Box)(({ theme, compact }) => ({
  "& h1": {
    fontSize: compact ? "0.95rem" : "1.25rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: compact ? 2 : 4,
  },
  "& p": {
    fontSize: compact ? "0.7rem" : "0.875rem",
    color: theme.palette.text.secondary,
  },
}));

export const FixedTableCell = styled(StyledTableCell)(
  ({ theme, position, offset, isHeader }) => ({
    position: "sticky",
    [position]: offset,
    zIndex: isHeader ? 3 : 2,
    backgroundColor: isHeader ? "rgb(44, 52, 123)" : theme.palette.background.paper,
    ...(position === "left" && {
      borderRight: `1px solid ${theme.palette.divider}`,
    }),
    ...(position === "right" && {
      borderLeft: `1px solid ${theme.palette.divider}`,
    }),
  })
);

export const FilterContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
  alignItems: "center",
  "& > *": {
    flexShrink: 0,
  },
}));

export const FilterButton = styled(Button, {
  shouldForwardProp: (prop) =>
    !["active", "sendIcon", "hasFilters"].includes(prop),
})(({ theme, active, hasFilters }) => ({
  backgroundColor: hasFilters
    ? alpha(theme.palette.primary.main, 0.12)
    : active
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.background.paper, 0.8),
  border: `1px solid ${hasFilters
      ? theme.palette.primary.main
      : active
        ? theme.palette.primary.main
        : alpha(theme.palette.divider, 0.8)
    }`,
  color: hasFilters
    ? theme.palette.primary.main
    : active
      ? theme.palette.primary.main
      : theme.palette.text.secondary,
  textTransform: "none",
  padding: "3px 10px",
  minHeight: 28,
  fontSize: "0.75rem",
  lineHeight: 1,
  display: "flex",
  alignItems: "center",
  gap: 6,
  borderRadius: 6,
  boxShadow:
    active || hasFilters
      ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`
      : "none",
  transition: "all 150ms ease",

  "& .filter-icon": {
    fontSize: "1rem",
    width: 16,
    height: 16,
    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
    opacity: 0.9,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& > svg": {
      fontSize: "1rem",
      width: 16,
      height: 16,
    },
  },

  "& .send-icon": {
    fontSize: "0.85rem",
    width: 16,
    height: 16,
    marginLeft: 2,
    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
    opacity: 0.8,
    transform: "rotate(0deg)",
    transition: "transform 200ms ease",
  },

  '&[aria-expanded="true"]': {
    "& .send-icon": {
      transform: "rotate(180deg)",
    },
  },

  "&:hover": {
    backgroundColor: active
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.action.hover, 0.05),
    borderColor: active
      ? theme.palette.primary.main
      : theme.palette.primary.light,
    "& .filter-icon, & .send-icon": {
      opacity: 1,
    },
  },

  "& .clear-icon": {
    fontSize: "0.75rem",
    width: 14,
    height: 14,
    marginLeft: 2,
    color: theme.palette.text.secondary,
    opacity: 0.8,
    cursor: "pointer",
    transition: "all 150ms ease",

    "&:hover": {
      opacity: 1,
      color: theme.palette.error.main,
    },
  },
}));

export const FilterCount = styled("span")(({ theme }) => ({
  minWidth: 18,
  height: 18,
  padding: "0 6px",
  borderRadius: 9,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontSize: "0.65rem",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: 4,
}));

export const FilterBadge = styled("span")(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: "50%",
  padding: "2px 6px",
  fontSize: "0.75rem",
  marginLeft: theme.spacing(0.5),
}));

export const FilterPopoverContent = styled(Box)(({ theme }) => ({
  padding: 0,
  minWidth: 220,
  maxWidth: 280,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 6,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  overflow: "hidden",
}));

export const FilterHeader = styled(Box)(({ theme }) => ({
  padding: "8px 12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.default, 0.4)
      : alpha(theme.palette.grey[50], 0.7),

  "& .MuiTypography-subtitle2": {
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: theme.palette.text.secondary,
  },

  "& .MuiIconButton-root": {
    padding: 4,
    marginRight: -4,
    "&:hover": {
      backgroundColor: alpha(theme.palette.action.hover, 0.08),
    },
    "& .MuiSvgIcon-root": {
      fontSize: "0.9rem",
      color: theme.palette.text.secondary,
    },
  },
}));

export const FilterOptions = styled(Box)(({ theme }) => ({
  maxHeight: 240,
  overflow: "auto",
  padding: "6px",

  "& .MuiMenuItem-root": {
    padding: "4px 6px",
    minHeight: 28,
    borderRadius: 4,
    marginBottom: 1,

    "&:hover": {
      backgroundColor: alpha(theme.palette.action.hover, 0.06),
    },

    "& .MuiCheckbox-root": {
      padding: 2,
      marginRight: 6,
      "& .MuiSvgIcon-root": {
        fontSize: "0.9rem",
      },
    },

    "& .MuiTypography-root": {
      fontSize: "0.7rem",
      color: theme.palette.text.primary,
    },
  },

  "&::-webkit-scrollbar": {
    width: 4,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: alpha(theme.palette.text.disabled, 0.2),
    borderRadius: 2,
    "&:hover": {
      background: alpha(theme.palette.text.disabled, 0.3),
    },
  },
}));

export const ClearAllButton = styled(Button)(({ theme }) => ({
  padding: "3px 8px",
  minHeight: 28,
  fontSize: "0.75rem",
  color: theme.palette.error.main,
  borderColor: theme.palette.error.main,
  borderRadius: 6,
  marginLeft: 4,

  "&:hover": {
    backgroundColor: alpha(theme.palette.error.main, 0.04),
    borderColor: theme.palette.error.main,
  },

  "& .MuiSvgIcon-root": {
    fontSize: "0.85rem",
    marginRight: 4,
  },
}));
